<script>
  import { createEventDispatcher, onDestroy } from "svelte";
  import { Geocoder } from "@beyonk/svelte-mapbox";
  import { Button, TextInput } from "carbon-components-svelte";
  import ChevronLeft from "carbon-icons-svelte/lib/ChevronLeft.svelte";
  import WarningFilled from "carbon-icons-svelte/lib/WarningFilled.svelte";
  import CheckmarkFilled from "carbon-icons-svelte/lib/CheckmarkFilled.svelte";

  import {
    yards,
    routeSources,
    loadAddressText,
    loadAddresCoordinate,
    quoteSelectedMaterial,
    destinationModeStore,
    customDisposeSiteStore,
    selectedSavedSiteStore,
  } from "../../../stores";
  import { mapDirections, removeLines } from "../distance";
  import { validateFullAddress, parseMapboxAddress } from "../../../helpers";
  import { saveYard, yardMaterials } from "../../../data";
  import { mapbox } from "../../mapbox.js";

  import FilterYard from "./FilterYard.svelte";
  import CompactYardList from "./CompactYardList.svelte";

  export let map;

  let defaultAddress = "";
  let accurateRoutes = 0;
  let addressEvent = null;
  let isMapping = false;
  let addressWarning = "";
  let addressValidation = null;
  let isResolvingLoadCoords = false;

  // Destination counts
  $: commercialCount = ($yards || []).filter(
    (y) => !y.location_type || y.location_type === "commercial"
  ).length;
  $: savedSitesList = ($yards || []).filter(
    (y) => y.location_type === "private_site"
  );
  $: privateCount = savedSitesList.length;

  // Load site coordinate & address validity (truly reactive)
  $: hasValidLoadCoords = Boolean(
    Array.isArray($loadAddresCoordinate) &&
      $loadAddresCoordinate.length >= 2 &&
      typeof $loadAddresCoordinate[0] === "number" &&
      !isNaN($loadAddresCoordinate[0]) &&
      typeof $loadAddresCoordinate[1] === "number" &&
      !isNaN($loadAddresCoordinate[1])
  );

  $: hasValidLoadAddress = Boolean(
    !addressWarning &&
      (hasValidLoadCoords ||
        (addressValidation && addressValidation.valid) ||
        Boolean(addressEvent) ||
        Boolean($loadAddressText && validateFullAddress($loadAddressText).valid))
  );

  $: disableMapRouteButton = !hasValidLoadAddress;

  // Auto-validate and auto-resolve load coordinates if addressText is present
  $: if ($loadAddressText && !addressWarning) {
    const val = validateFullAddress($loadAddressText);
    if (val.valid && (!addressValidation || !addressValidation.valid)) {
      addressValidation = { valid: true };
    }
  }

  $: if ($loadAddressText && !hasValidLoadCoords && !addressWarning) {
    resolveLoadAddressCoords($loadAddressText);
  }

  // Saved Sites selection state
  let selectedSavedSiteId = "";
  $: selectedSavedSite = savedSitesList.find(
    (s) => String(s.id) === String(selectedSavedSiteId)
  );

  // Can map route flags for each mode
  $: canMapCommercialRoute = Boolean(hasValidLoadAddress && !isMapping);

  $: canMapSavedSite = Boolean(
    hasValidLoadAddress &&
      selectedSavedSite &&
      selectedSavedSite.address &&
      selectedSavedSite.address.coordinates &&
      !isMapping
  );

  // Custom Site form states
  let customSiteName = "";
  let customAddressText = "";
  let customAddressCoords = null; // { lat, lng }
  let customAddressStreet = "";
  let customAddressSuburb = "";
  let customAddressState = "NSW";
  let material_name =
    $quoteSelectedMaterial && $quoteSelectedMaterial !== "All"
      ? $quoteSelectedMaterial
      : "";
  let customUhf = "";
  let customGeofence = "200";
  let customFeeType = "per_tonne"; // "per_tonne" | "per_load"
  let customUnitPrice = "150";
  let saveToPrivateSites = false;
  let isSavingPrivateSite = false;
  let customAddressWarning = "";

  $: if (!material_name && $quoteSelectedMaterial && $quoteSelectedMaterial !== "All") {
    material_name = $quoteSelectedMaterial;
  }

  $: canMapCustomRoute = Boolean(
    hasValidLoadAddress &&
      customAddressCoords &&
      customAddressCoords.lat &&
      customAddressCoords.lng &&
      !customAddressWarning &&
      !isMapping
  );

  // Floating dispose site marker reference on map
  let disposeMarker = null;

  const dispatch = createEventDispatcher();

  /**
   * Automatically resolves coordinates from Mapbox Geocoding API if address string is available.
   */
  async function resolveLoadAddressCoords(addrText) {
    if (!addrText || isResolvingLoadCoords || typeof MAPBOX_TOKEN === "undefined") return null;
    try {
      isResolvingLoadCoords = true;
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(addrText)}.json?country=AU&types=address&access_token=${MAPBOX_TOKEN}`
      );
      const data = await res.json();
      if (data?.features?.[0]?.geometry?.coordinates) {
        const coords = data.features[0].geometry.coordinates;
        if (Array.isArray(coords) && coords.length >= 2) {
          $loadAddresCoordinate = coords;
          dispatch("address-change", {
            result: data.features[0],
          });
          return coords;
        }
      }
    } catch (err) {
      console.warn("Failed to geocode load address:", err);
    } finally {
      isResolvingLoadCoords = false;
    }
    return null;
  }

  function onAddressChange(e) {
    addressEvent = e;
    const result = e.detail?.result || e.detail?.detail?.result;
    const placeName = result?.place_name || "";
    const isAddressFeature = result?.place_type?.includes("address");
    const validation = validateFullAddress(placeName);

    if (isAddressFeature || validation.valid) {
      addressValidation = { valid: true };
      addressWarning = "";
      dispatch("address-change", e);
      if ($yards && $yards.length > 0) {
        accurateRoutes = $yards.length;
      }
    } else {
      addressValidation = { valid: false };
      addressWarning =
        "Full address required: MUST include street and suburb (e.g. Church St, Parramatta). Street number is optional.";
      dispatch("address-change", e);
    }
  }

  function removeAddress() {
    addressEvent = null;
    addressValidation = null;
    addressWarning = "";
    $loadAddresCoordinate = null;
    $loadAddressText = "";
    dispatch("clear-address");
    accurateRoutes = 0;
    dispatch("routes-cleared");
  }

  function onYardsListUpdated() {
    if ($yards && $yards.length > 0) {
      accurateRoutes = $yards.length;
    }
    dispatch("routes-cleared");
    dispatch("yards-list-updated");
  }

  /**
   * Updates or places a distinctive Mapbox marker for the custom/saved disposal site.
   */
  function updateDisposeMarker(siteName, coords, addressText) {
    if (!map || !coords) return;
    if (disposeMarker) {
      disposeMarker.remove();
      disposeMarker = null;
    }
    const lng = coords.lng ?? coords.longitude ?? coords[0];
    const lat = coords.lat ?? coords.latitude ?? coords[1];
    if (lng == null || lat == null) return;

    const el = document.createElement("div");
    el.className = "marker map-icon-marker custom-site-marker";
    el.style.backgroundColor = "#ea580c";
    el.style.color = "#ffffff";
    el.style.width = "32px";
    el.style.height = "32px";
    el.style.borderRadius = "50%";
    el.style.display = "flex";
    el.style.alignItems = "center";
    el.style.justifyContent = "center";
    el.style.fontSize = "16px";
    el.style.boxShadow = "0 2px 8px rgba(0,0,0,0.35)";
    el.style.border = "2px solid #ffffff";
    el.style.cursor = "pointer";
    el.innerText = "🏗️";

    const popup = new mapbox.Popup({ offset: 25 }).setHTML(
      `<b>${siteName}</b><br><span style="font-size:12px;color:#475569;">${addressText || ""}</span>`
    );

    disposeMarker = new mapbox.Marker(el, { anchor: "bottom" })
      .setLngLat([lng, lat])
      .setPopup(popup)
      .addTo(map);
  }

  /**
   * Adjusts the map view to cleanly display both origin and destination within bounds.
   */
  function fitBoundsToRoute(origin, destination) {
    if (!map || !origin || !destination) return;
    try {
      const minLng = Math.min(origin[0], destination[0]);
      const maxLng = Math.max(origin[0], destination[0]);
      const minLat = Math.min(origin[1], destination[1]);
      const maxLat = Math.max(origin[1], destination[1]);

      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: { top: 90, bottom: 90, left: 140, right: 90 },
          maxZoom: 14,
          duration: 1000,
        }
      );
    } catch (e) {
      console.warn("fitBounds failed:", e);
    }
  }

  /**
   * Handles switching destination mode between commercial yards, saved private sites, and custom ad-hoc sites.
   */
  function setDestinationMode(mode) {
    $destinationModeStore = mode;
    removeLines(map);
    if (mode === "commercial") {
      if (disposeMarker) {
        disposeMarker.remove();
        disposeMarker = null;
      }
    } else if (mode === "private_site") {
      if (selectedSavedSite && selectedSavedSite.address?.coordinates) {
        updateDisposeMarker(
          selectedSavedSite.name,
          selectedSavedSite.address.coordinates,
          selectedSavedSite.address.street
            ? `${selectedSavedSite.address.street}, ${selectedSavedSite.address.suburb || ""}`
            : selectedSavedSite.address.suburb
        );
      } else if (disposeMarker) {
        disposeMarker.remove();
        disposeMarker = null;
      }
    } else if (mode === "ad_hoc_site") {
      if (customAddressCoords) {
        const sName =
          customSiteName ||
          (customAddressSuburb
            ? `${customAddressSuburb} Disposal Site`
            : "Custom Disposal Site");
        updateDisposeMarker(sName, customAddressCoords, customAddressText);
      } else if (disposeMarker) {
        disposeMarker.remove();
        disposeMarker = null;
      }
    }
    dispatch("routes-cleared");
  }

  function onSelectSavedSite(e) {
    selectedSavedSiteId = e.target.value;
    const site = savedSitesList.find(
      (s) => String(s.id) === String(selectedSavedSiteId)
    );
    if (site && site.address && site.address.coordinates) {
      updateDisposeMarker(
        site.name,
        site.address.coordinates,
        site.address.street
          ? `${site.address.street}, ${site.address.suburb || ""}`
          : site.address.suburb
      );
      if (map) {
        map.flyTo({
          center: [site.address.coordinates.lng, site.address.coordinates.lat],
          zoom: 12,
        });
      }
    }
  }

  function onCustomAddressResult(e) {
    const result = e.detail?.result || e.detail?.detail?.result;
    if (!result) return;
    const parsed = parseMapboxAddress(result);
    if (parsed) {
      customAddressText = parsed.place_name || "";
      customAddressCoords = parsed.coordinates;
      customAddressStreet = parsed.street || "";
      customAddressSuburb = parsed.suburb || "";
      customAddressState = parsed.state || "NSW";
      customAddressPostcode = parsed.postcode || "";

      if (parsed.is_full_address) {
        customAddressWarning = "";
        const sName =
          customSiteName ||
          (customAddressSuburb
            ? `${customAddressSuburb} Disposal Site`
            : "Custom Disposal Site");
        updateDisposeMarker(sName, customAddressCoords, customAddressText);
        if (map && customAddressCoords.lng && customAddressCoords.lat) {
          map.flyTo({
            center: [customAddressCoords.lng, customAddressCoords.lat],
            zoom: 13,
          });
        }
      } else {
        customAddressWarning =
          "Full address required: MUST include street and suburb (e.g. Church St, Parramatta).";
      }
    }
  }

  function onClearCustomAddress() {
    customAddressText = "";
    customAddressCoords = null;
    customAddressStreet = "";
    customAddressSuburb = "";
    customAddressWarning = "";
    if (disposeMarker) {
      disposeMarker.remove();
      disposeMarker = null;
    }
    removeLines(map);
    dispatch("routes-cleared");
  }

  /**
   * Maps driving routes for filtered commercial yards using Mapbox Directions API.
   */
  async function callMapDirections() {
    let originCoord = $loadAddresCoordinate;
    if ((!originCoord || !Array.isArray(originCoord) || originCoord.length < 2) && $loadAddressText) {
      originCoord = await resolveLoadAddressCoords($loadAddressText);
    }
    if (!originCoord || !Array.isArray(originCoord) || originCoord.length < 2) {
      addressWarning =
        "Please enter and select a valid load site address first.";
      return;
    }

    if (addressEvent) {
      dispatch("address-change", addressEvent);
    }
    isMapping = true;

    try {
      const count = await mapDirections(map, null, originCoord);
      accurateRoutes = count || ($routeSources ? $routeSources.length : 0);
      dispatch("routes-displayed", { count: accurateRoutes });
    } catch (err) {
      console.error("Error mapping directions:", err);
    } finally {
      isMapping = false;
    }
  }

  /**
   * Maps driving route from Load Site to the selected Saved Private Site.
   */
  async function callMapDirectionsForSavedSite() {
    if (!selectedSavedSite) return;
    let originCoord = $loadAddresCoordinate;
    if ((!originCoord || !Array.isArray(originCoord) || originCoord.length < 2) && $loadAddressText) {
      originCoord = await resolveLoadAddressCoords($loadAddressText);
    }
    if (!originCoord || !Array.isArray(originCoord) || originCoord.length < 2) {
      addressWarning =
        "Please enter and select a valid load site address first.";
      return;
    }

    if (addressEvent) {
      dispatch("address-change", addressEvent);
    }

    isMapping = true;
    try {
      const destLng =
        selectedSavedSite.address?.coordinates?.lng ??
        selectedSavedSite.address?.coordinates?.longitude;
      const destLat =
        selectedSavedSite.address?.coordinates?.lat ??
        selectedSavedSite.address?.coordinates?.latitude;

      if (destLng != null && destLat != null) {
        updateDisposeMarker(
          selectedSavedSite.name,
          { lng: destLng, lat: destLat },
          selectedSavedSite.address?.street
            ? `${selectedSavedSite.address.street}, ${selectedSavedSite.address.suburb || ""}`
            : selectedSavedSite.address?.suburb
        );
      }

      $selectedSavedSiteStore = selectedSavedSite;
      $yards = [
        ...($yards || []).filter((y) => String(y.id) !== String(selectedSavedSite.id)),
        selectedSavedSite,
      ];

      const count = await mapDirections(map, [selectedSavedSite], originCoord);

      if (destLng != null && destLat != null) {
        fitBoundsToRoute(originCoord, [destLng, destLat]);
      }

      dispatch("routes-displayed", { count: count || 1 });
    } catch (err) {
      console.error("Error mapping saved site route:", err);
    } finally {
      isMapping = false;
    }
  }

  /**
   * Maps driving route from Load Site to the configured Custom Site.
   */
  async function callMapDirectionsForCustomSite() {
    if (
      !customAddressCoords ||
      !customAddressCoords.lng ||
      !customAddressCoords.lat
    ) {
      customAddressWarning =
        "Please search and select a valid disposal site address.";
      return;
    }
    let originCoord = $loadAddresCoordinate;
    if ((!originCoord || !Array.isArray(originCoord) || originCoord.length < 2) && $loadAddressText) {
      originCoord = await resolveLoadAddressCoords($loadAddressText);
    }
    if (!originCoord || !Array.isArray(originCoord) || originCoord.length < 2) {
      addressWarning =
        "Please enter and select a valid load site address first.";
      return;
    }

    if (addressEvent) {
      dispatch("address-change", addressEvent);
    }

    isMapping = true;
    try {
      const siteName =
        customSiteName && customSiteName.trim()
          ? customSiteName.trim()
          : customAddressSuburb
            ? `${customAddressSuburb} Disposal Site`
            : "Custom Disposal Site";

      const customYardId = "custom_" + Date.now();
      const effectiveMatName =
        material_name && material_name.trim()
          ? material_name.trim()
          : ($quoteSelectedMaterial && $quoteSelectedMaterial !== "All"
            ? $quoteSelectedMaterial
            : "General Material");

      if (effectiveMatName && effectiveMatName !== "General Material") {
        $quoteSelectedMaterial = effectiveMatName;
      }

      const customYardObj = {
        id: customYardId,
        name: siteName,
        material_name: effectiveMatName,
        material: effectiveMatName,
        location_type: saveToPrivateSites ? "private_site" : "ad_hoc_site",
        destination_type: saveToPrivateSites ? "private_site" : "ad_hoc_site",
        disposal_fee_type: customFeeType,
        disposal_unit_price: Number(customUnitPrice) || 150,
        fee_type: customFeeType,
        unit_price: Number(customUnitPrice) || 150,
        default_uhf_channel: customUhf ? customUhf.trim() : null,
        geofence_radius: Number(customGeofence) || 200,
        address: {
          street: customAddressStreet || customAddressText,
          suburb: customAddressSuburb || "",
          state: customAddressState || "NSW",
          postcode: customAddressPostcode || "",
          coordinates: {
            lat: customAddressCoords.lat,
            lng: customAddressCoords.lng,
          },
        },
        accepts: [
          {
            name: effectiveMatName,
            material_name: effectiveMatName,
            prices: [
              {
                unit: customFeeType === "per_load" ? "load" : "tonnes",
                price: Number(customUnitPrice) || 150,
              },
            ],
          },
        ],
      };

      if (saveToPrivateSites) {
        try {
          isSavingPrivateSite = true;
          const payload = {
            name: siteName,
            location_type: "private_site",
            default_uhf_channel: customUhf ? customUhf.trim() : null,
            geofence_radius: Number(customGeofence) || 200,
            is_active: true,
            address: customYardObj.address,
            accepts: customYardObj.accepts,
          };
          const saved = await saveYard(payload);
          if (saved && (saved.id || saved._id)) {
            customYardObj.id = saved.id || saved._id;
          }
        } catch (err) {
          console.warn("Failed to persist reusable private site:", err);
        } finally {
          isSavingPrivateSite = false;
        }
      }

      // Add to $yards store so other components can reference it
      $yards = [
        ...($yards || []).filter((y) => y.id !== customYardObj.id),
        customYardObj,
      ];
      $customDisposeSiteStore = customYardObj;

      updateDisposeMarker(siteName, customAddressCoords, customAddressText);

      const count = await mapDirections(map, [customYardObj], originCoord);

      fitBoundsToRoute(originCoord, [
        customAddressCoords.lng,
        customAddressCoords.lat,
      ]);

      dispatch("routes-displayed", { count: count || 1 });
    } catch (err) {
      console.error("Error mapping custom site route:", err);
    } finally {
      isMapping = false;
    }
  }

  function hideThisWindow() {
    dispatch("hideLoadAddressWindow", true);
  }

  onDestroy(() => {
    if (disposeMarker) {
      disposeMarker.remove();
      disposeMarker = null;
    }
  });
</script>

<div class="quote-box">
  <div class="card-header">
    <div class="header-info">
      <h2 class="card-title">Quote & Route Planner</h2>
      <span class="card-subtitle"
        >Find closest disposal yards & optimize haulage</span
      >
    </div>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <button
      class="close-card-btn"
      on:click={hideThisWindow}
      title="Collapse planner"
      type="button"
    >
      <ChevronLeft size={20} />
    </button>
  </div>

  <div class="card-body">
    <!-- Load Site Location Section -->
    <div class="section-block">
      <div class="field-header-row">
        <label class="bx--label mb-0" for="load-address-geo"
          >Load Site Location</label
        >
        <span class="address-req-tag">Street &amp; Suburb Required</span>
      </div>
      <div id="load-address-geo" class="geocoder-wrapper">
        <Geocoder
          accessToken={MAPBOX_TOKEN}
          on:result={(e) => onAddressChange(e)}
          on:clear={removeAddress}
          value={defaultAddress || $loadAddressText}
          geocoder
          options={{
            countries: "AU",
            types: "address",
            placeholder: "Search site address (e.g. Church St, Parramatta)...",
          }}
        />
      </div>
      {#if addressWarning}
        <div class="address-geo-warning">
          <WarningFilled size={14} />
          <span>{addressWarning}</span>
        </div>
      {:else if hasValidLoadAddress}
        <div class="address-geo-success">
          <CheckmarkFilled size={14} />
          <span>
            Load address verified
            {#if hasValidLoadCoords}
              [{$loadAddresCoordinate[0].toFixed(5)}, {$loadAddresCoordinate[1].toFixed(5)}]
            {:else}
              (street and suburb present)
            {/if}
          </span>
        </div>
      {/if}
    </div>

    <!-- 3-Way Destination Mode Selector (Placed directly under Load Site Location) -->
    <div class="destination-mode-bar">
      <button
        type="button"
        class="mode-btn {$destinationModeStore === 'commercial' ? 'active' : ''}"
        on:click={() => setDestinationMode("commercial")}
      >
        <span class="mode-icon">🏭</span>
        <span>Commercial Yards</span>
        <span class="mode-count">{commercialCount}</span>
      </button>

      <button
        type="button"
        class="mode-btn {$destinationModeStore === 'private_site' ? 'active' : ''}"
        on:click={() => setDestinationMode("private_site")}
      >
        <span class="mode-icon">🏗️</span>
        <span>Saved Sites</span>
        <span class="mode-count">{privateCount}</span>
      </button>

      <button
        type="button"
        class="mode-btn {$destinationModeStore === 'ad_hoc_site' ? 'active' : ''}"
        on:click={() => setDestinationMode("ad_hoc_site")}
      >
        <span class="mode-icon">➕</span>
        <span>Custom Site</span>
      </button>
    </div>

    <!-- Mode 1: Commercial Yards (Material dropdown & Yards list appear ONLY here) -->
    {#if $destinationModeStore === "commercial"}
      <div class="section-block">
        <FilterYard
          on:filter-yards
          {map}
          on:yards-list-updated={onYardsListUpdated}
        >
          <div slot="action" class="map-route-action">
            <Button
              disabled={!canMapCommercialRoute}
              on:click={callMapDirections}
              size="field"
              kind="secondary"
            >
              {isMapping ? "Mapping Routes..." : "Map Routes"}
            </Button>
          </div>
        </FilterYard>
      </div>

      <div class="section-block yards-list-block">
        <CompactYardList />
      </div>

    <!-- Mode 2: Saved Sites -->
    {:else if $destinationModeStore === "private_site"}
      <div class="section-block saved-sites-container">
        <div class="field-header-row">
          <label class="bx--label mb-0" for="saved-site-select"
            >Select Saved Disposal Site</label
          >
          <span class="site-count-badge">{savedSitesList.length} Available</span>
        </div>

        {#if savedSitesList.length === 0}
          <div class="empty-saved-box">
            <div class="empty-icon">🏗️</div>
            <div class="empty-title">No Saved Private Sites Found</div>
            <div class="empty-desc">
              You haven't saved any private sites yet. Switch to the <strong>Custom Site</strong> tab to configure a disposal site and check <em>"Save as Reusable Private Site"</em>.
            </div>
            <button
              type="button"
              class="switch-to-custom-btn"
              on:click={() => setDestinationMode("ad_hoc_site")}
            >
              ➕ Configure Custom Site
            </button>
          </div>
        {:else}
          <div class="saved-select-wrapper">
            <select
              id="saved-site-select"
              class="saved-sites-dropdown"
              bind:value={selectedSavedSiteId}
              on:change={onSelectSavedSite}
            >
              <option value="">-- Choose a Saved Site ({savedSitesList.length}) --</option>
              {#each savedSitesList as site}
                <option value={site.id}
                  >{site.name} — {site.address?.suburb || site.address?.street || "Site"}</option
                >
              {/each}
            </select>
          </div>

          {#if selectedSavedSite}
            <div class="selected-site-details-card">
              <div class="site-card-header">
                <div class="site-name-title">{selectedSavedSite.name}</div>
                <span class="site-type-badge">Saved Private Site</span>
              </div>
              <div class="site-detail-row">
                <span class="detail-label">📍 Address:</span>
                <span class="detail-value"
                  >{selectedSavedSite.address?.street
                    ? `${selectedSavedSite.address.street}, ${selectedSavedSite.address.suburb || ""} ${selectedSavedSite.address.state || ""}`
                    : selectedSavedSite.address?.suburb || "Coordinates confirmed"}</span
                >
              </div>
              {#if selectedSavedSite.default_uhf_channel}
                <div class="site-detail-row">
                  <span class="detail-label">📻 UHF:</span>
                  <span class="detail-value">Ch {selectedSavedSite.default_uhf_channel}</span>
                </div>
              {/if}
              {#if selectedSavedSite.geofence_radius}
                <div class="site-detail-row">
                  <span class="detail-label">⭕ Geofence:</span>
                  <span class="detail-value">{selectedSavedSite.geofence_radius}m</span>
                </div>
              {/if}
            </div>
          {/if}

          <div class="map-route-action mt-2">
            <Button
              disabled={!canMapSavedSite}
              on:click={callMapDirectionsForSavedSite}
              size="field"
              kind="secondary"
            >
              {isMapping ? "Mapping Route..." : "Map Route"}
            </Button>
          </div>
        {/if}
      </div>

    <!-- Mode 3: Custom Site -->
    {:else if $destinationModeStore === "ad_hoc_site"}
      <div class="section-block custom-site-container">
        <div class="custom-fields-group">
          <!-- Dispose Site Geocoder -->
          <div class="custom-field-item">
            <div class="field-header-row">
              <label class="bx--label mb-0" for="custom-dispose-geo"
                >Dispose Site Location *</label
              >
              <span class="address-req-tag">Street &amp; Suburb Required</span>
            </div>
            <div id="custom-dispose-geo" class="geocoder-wrapper">
              <Geocoder
                accessToken={MAPBOX_TOKEN}
                on:result={onCustomAddressResult}
                on:clear={onClearCustomAddress}
                value={customAddressText}
                geocoder
                options={{
                  countries: "AU",
                  types: "address",
                  placeholder:
                    "Search dispose address (e.g. Church St, Parramatta)...",
                }}
              />
            </div>
            {#if customAddressWarning}
              <div class="address-geo-warning">
                <WarningFilled size={14} />
                <span>{customAddressWarning}</span>
              </div>
            {:else if customAddressCoords}
              <div class="address-geo-success">
                <CheckmarkFilled size={14} />
                <span
                  >Disposal address verified [{customAddressCoords.lng.toFixed(
                    5
                  )}, {customAddressCoords.lat.toFixed(5)}]</span
                >
              </div>
            {/if}
          </div>

          <!-- Dispose Site Name -->
          <div class="custom-field-item">
            <TextInput
              light
              size="sm"
              labelText="Dispose Site / Facility Name"
              bind:value={customSiteName}
              placeholder="Optional (defaults to suburb name, e.g. Mulgoa Disposal Site)"
              on:input={() => {
                if (disposeMarker && customAddressCoords) {
                  updateDisposeMarker(
                    customSiteName ||
                      (customAddressSuburb
                        ? `${customAddressSuburb} Disposal Site`
                        : "Custom Disposal Site"),
                    customAddressCoords,
                    customAddressText
                  );
                }
              }}
            />
          </div>

          <!-- Disposal Fee Type & Rate -->
          <div class="custom-field-row">
            <div class="custom-half-col">
              <label class="bx--label mb-1" for="fee-type-group">Disposal Fee Type</label>
              <div id="fee-type-group" class="fee-type-segmented">
                <button
                  type="button"
                  class="fee-seg-btn {customFeeType === 'per_tonne' ? 'active' : ''}"
                  on:click={() => (customFeeType = "per_tonne")}
                >
                  $/tonne
                </button>
                <button
                  type="button"
                  class="fee-seg-btn {customFeeType === 'per_load' ? 'active' : ''}"
                  on:click={() => (customFeeType = "per_load")}
                >
                  $/load
                </button>
              </div>
            </div>

            <div class="custom-half-col">
              <TextInput
                light
                size="sm"
                labelText={customFeeType === "per_load"
                  ? "Rate per Load ($)"
                  : "Rate per Tonne ($)"}
                bind:value={customUnitPrice}
                placeholder="150"
              />
            </div>
          </div>

          <!-- Material Field -->
          <div class="custom-field-item">
            <TextInput
              light
              size="sm"
              labelText="Material"
              bind:value={material_name}
              placeholder="e.g. VENM, ENM, Clean Fill"
              list="custom-material-list"
            />
            <datalist id="custom-material-list">
              {#each (yardMaterials || []) as ym}
                <option value={ym} />
              {/each}
            </datalist>
          </div>

          <!-- Save as Reusable Private Site Checkbox -->
          <label class="save-private-checkbox">
            <input type="checkbox" bind:checked={saveToPrivateSites} />
            <span
              >Save as Reusable Private Site (available for future quotes)</span
            >
          </label>

          <!-- Map Route Button -->
          <div class="map-route-action mt-2">
            <Button
              disabled={!canMapCustomRoute}
              on:click={callMapDirectionsForCustomSite}
              size="field"
              kind="secondary"
            >
              {isMapping ? "Mapping Route..." : "Map Route"}
            </Button>
          </div>
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .quote-box {
    position: absolute;
    top: 4rem;
    left: 1rem;
    width: 580px;
    max-width: calc(100vw - 2rem);
    max-height: calc(100% - 5rem);
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(16px);
    border: 1px solid var(--tt-slate-200);
    border-radius: var(--tt-radius-lg);
    box-shadow: var(--tt-shadow-xl);
    z-index: 1200;
    pointer-events: auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: fadeIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.875rem 1.125rem;
    border-bottom: 1px solid var(--tt-slate-200);
    background-color: var(--tt-slate-50);
  }

  .card-title {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 700;
    color: var(--tt-slate-900);
    letter-spacing: -0.01em;
  }

  .card-subtitle {
    font-size: 0.75rem;
    color: var(--tt-slate-500);
  }

  .close-card-btn {
    width: 32px;
    height: 32px;
    border-radius: var(--tt-radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    color: var(--tt-slate-500);
    cursor: pointer;
    transition: all 0.15s ease;
    margin: 0;
    padding: 0;
  }

  .close-card-btn:hover {
    background: var(--tt-slate-200);
    color: var(--tt-slate-800);
  }

  .card-body {
    padding: 1rem 1.125rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .section-block {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  /* 3-Way Destination Mode Selector */
  .destination-mode-bar {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px;
    background: #f1f5f9;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    margin: 2px 0 4px 0;
  }

  .mode-btn {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 7px 10px;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease-in-out;
    white-space: nowrap;
  }

  .mode-btn:hover {
    color: #1e293b;
    background: rgba(255, 255, 255, 0.6);
  }

  .mode-btn.active {
    background: #ffffff;
    color: #0f172a;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .mode-icon {
    font-size: 14px;
    line-height: 1;
  }

  .mode-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    padding: 1px 5px;
    border-radius: 999px;
    background: #e2e8f0;
    color: #475569;
  }

  .mode-btn.active .mode-count {
    background: #eff6ff;
    color: #2563eb;
  }

  .geocoder-wrapper {
    width: 100%;
  }

  .map-route-action {
    width: 100%;
    display: flex;
    align-items: flex-end;
  }

  :global(.map-route-action .bx--btn) {
    width: 100% !important;
    justify-content: center !important;
    border-radius: var(--tt-radius-sm) !important;
    min-height: 40px !important;
    height: 40px !important;
    padding-left: 12px !important;
    padding-right: 12px !important;
    font-weight: 600 !important;
  }

  .field-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 4px;
  }

  :global(.field-header-row .bx--label.mb-0) {
    margin-bottom: 0 !important;
  }

  .address-req-tag {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 6px;
    border-radius: 4px;
    background: #eff6ff;
    color: #2563eb;
    border: 1px solid #bfdbfe;
  }

  .site-count-badge {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 6px;
    border-radius: 4px;
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #cbd5e1;
  }

  .address-geo-warning {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
    font-weight: 500;
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
    padding: 6px 10px;
    border-radius: 6px;
    margin-top: 4px;
    line-height: 1.3;
  }

  .address-geo-success {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72rem;
    font-weight: 600;
    color: #15803d;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    padding: 4px 8px;
    border-radius: 6px;
    margin-top: 4px;
  }

  /* Saved Sites Styling */
  .saved-sites-container {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .saved-select-wrapper {
    width: 100%;
  }

  .saved-sites-dropdown {
    width: 100%;
    height: 40px;
    padding: 0 10px;
    font-size: 13px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background-color: #ffffff;
    color: #0f172a;
    outline: none;
    cursor: pointer;
  }

  .saved-sites-dropdown:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }

  .empty-saved-box {
    text-align: center;
    padding: 16px 12px;
    background: #ffffff;
    border: 1px dashed #cbd5e1;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }

  .empty-icon {
    font-size: 24px;
  }

  .empty-title {
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
  }

  .empty-desc {
    font-size: 12px;
    color: #64748b;
    max-width: 380px;
    line-height: 1.4;
  }

  .switch-to-custom-btn {
    margin-top: 6px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    background: #eff6ff;
    color: #2563eb;
    border: 1px solid #bfdbfe;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .switch-to-custom-btn:hover {
    background: #dbeafe;
  }

  .selected-site-details-card {
    background: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .site-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 2px;
  }

  .site-name-title {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
  }

  .site-type-badge {
    font-size: 10px;
    font-weight: 600;
    background: #eff6ff;
    color: #2563eb;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .site-detail-row {
    font-size: 12px;
    color: #475569;
    display: flex;
    gap: 6px;
    align-items: baseline;
  }

  .detail-label {
    font-weight: 600;
    color: #334155;
  }

  .detail-value {
    color: #64748b;
  }

  /* Custom Site Styling */
  .custom-site-container {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px;
  }

  .custom-fields-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .custom-field-item {
    width: 100%;
  }

  .custom-field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    align-items: end;
  }

  .custom-half-col {
    width: 100%;
  }

  .fee-type-segmented {
    display: flex;
    height: 32px;
    background: #e2e8f0;
    border-radius: 4px;
    padding: 2px;
    gap: 2px;
  }

  .fee-seg-btn {
    flex: 1;
    border: none;
    background: transparent;
    color: #475569;
    font-size: 11px;
    font-weight: 600;
    border-radius: 3px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .fee-seg-btn.active {
    background: #ffffff;
    color: #0f172a;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  .save-private-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #334155;
    font-weight: 500;
    cursor: pointer;
    margin: 4px 0 2px 0;
  }

  .save-private-checkbox input[type="checkbox"] {
    width: 15px;
    height: 15px;
    accent-color: #2563eb;
    cursor: pointer;
  }

  .mt-2 {
    margin-top: 8px;
  }
</style>
